import { useState, useEffect } from 'react'

interface SiteAuth {
  authed: boolean
  ts: number
}

const STORAGE_KEY = 'siteAuth'
const TTL_HOURS = 24

interface UseStagingAuthResult {
  authed: boolean
  isLoading: boolean
  setAuthed: (authed: boolean) => void
}

export const useStagingAuth = (): UseStagingAuthResult => {
  const [authed, setAuthedState] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)

  useEffect(() => {
    // Only run on staging builds
    if (!import.meta.env.VITE_USE_SITE_GATING) {
      setAuthedState(true)
      setIsLoading(false)
      return
    }

    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const siteAuth: SiteAuth = JSON.parse(stored)
        const now = Date.now()
        const isExpired = now - siteAuth.ts > TTL_HOURS * 60 * 60 * 1000

        if (siteAuth.authed && !isExpired) {
          setAuthedState(true)
          setIsLoading(false)
          return
        }
      }
    } catch (error) {
      console.warn('Failed to parse site auth from localStorage:', error)
    }

    setAuthedState(false)
    setIsLoading(false)
  }, [])

  const setAuthed = (authed: boolean) => {
    setAuthedState(authed)

    if (authed) {
      const siteAuth: SiteAuth = {
        authed: true,
        ts: Date.now(),
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(siteAuth))
    } else {
      localStorage.removeItem(STORAGE_KEY)
    }
  }

  return { authed, isLoading, setAuthed }
}

// Helper function to clear auth (handy for QA)
export const clearStagingAuth = () => {
  localStorage.removeItem(STORAGE_KEY)
}
